/**
 * Espelho (frontend) das regras de aviso de fatura do backend
 * (`@shared/utils/invoiceAlerts`): **em aberto** (`hasDebt` na fatura) e
 * **a vencer** (janela de D-3 do vencimento). Como o backend, comparamos por
 * DIA-CALENDÁRIO UTC — `dueDate` é data pura e o extrator a cria ao meio-dia
 * UTC, então a diferença em dias não muda com o fuso do navegador.
 *
 * Espelho intencional e pequeno (só o rótulo): o dado exibido é o mesmo que
 * originou o alerta do sino; mudar a janela exige mudar os dois lados.
 */
import { Invoice } from 'entities/Invoice';

export const INVOICE_DUE_WINDOW_DAYS = 3;

export type InvoiceAlertKind = 'open' | 'due';

export interface InvoiceAlert {
  kind: InvoiceAlertKind;
  label: string;
  daysLeft?: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const toUtcDay = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/** Dias-calendário até o vencimento (0 = hoje; negativo = vencido). */
export const daysUntilDue = (
  dueDate: Date | string,
  today: Date = new Date()
): number => {
  const parsed = dueDate instanceof Date ? dueDate : new Date(dueDate);

  return Math.round((toUtcDay(parsed) - toUtcDay(today)) / MS_PER_DAY);
};

/**
 * Aviso mais relevante da UC: fatura em aberto tem precedência sobre "a vencer".
 * Ignora meses pendentes (sem fatura importada, `invoiceValue < 0`).
 */
export const getInvoiceAlert = (
  invoiceList: Invoice[],
  today: Date = new Date()
): InvoiceAlert | null => {
  const invoices = invoiceList.filter(
    (invoice) => invoice.invoiceValue >= 0 && invoice.dueDate
  );

  if (!invoices.length) {
    return null;
  }

  const latest = [...invoices].sort((a, b) =>
    a.refMonth > b.refMonth ? -1 : 1
  )[0];

  if (latest?.hasDebt) {
    return { kind: 'open', label: 'Fatura em aberto' };
  }

  const dueSoon = invoices
    .map((invoice) => ({
      invoice,
      daysLeft: daysUntilDue(invoice.dueDate, today),
    }))
    .filter((x) => x.daysLeft >= 0 && x.daysLeft <= INVOICE_DUE_WINDOW_DAYS)
    .sort((a, b) => a.daysLeft - b.daysLeft)[0];

  if (!dueSoon) {
    return null;
  }

  return {
    kind: 'due',
    label:
      dueSoon.daysLeft === 0
        ? 'Vence hoje'
        : `Vence em ${dueSoon.daysLeft} dia${dueSoon.daysLeft > 1 ? 's' : ''}`,
    daysLeft: dueSoon.daysLeft,
  };
};
